/* src/components/sessions/AllSessionsTable.js */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, formatDuration } from '../dashboard/utils/formatters';
import { formatCombinedDifficulty } from '../../contexts/gameHelpers';
import '../animations.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlay, 
    faChevronUp, 
    faChevronDown,
    faArrowUp,
    faArrowDown,
    faEquals,
    faCalendarAlt,
    faClock,
    faPercentage,
    faChartLine,
    faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';

const AllSessionsTable = ({ sessions = [] }) => {
    const navigate = useNavigate();
    const [sortField, setSortField] = useState('recordedAt');
    const [sortDirection, setSortDirection] = useState('desc');
    
    // Handle replay navigation
    const handleReplay = (e, session) => {
        e.stopPropagation();
        const sessionId = session?.id;
        const hasValidReplayData = !!session?.replayData?.settings &&
            Array.isArray(session.replayData.events) &&
            session.replayData.startTime !== undefined;

        if (sessionId && hasValidReplayData) {
            navigate(`/replay/${sessionId}`);
        } else {
            console.warn("Replay unavailable:", { sessionId, hasValidReplayData });
            alert("Replay data is not available or incomplete for this session.");
        }
    };
    
    // Handle sorting
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if already sorting by this field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to descending
            setSortField(field);
            setSortDirection('desc');
        }
    };
    
    // Get sorted sessions
    const sortedSessions = React.useMemo(() => {
        if (!sessions.length) return [];
        
        return [...sessions].sort((a, b) => {
            // Special handling for date
            if (sortField === 'recordedAt') {
                const dateA = a.recordedAt ? new Date(a.recordedAt) : new Date(a.date || 0);
                const dateB = b.recordedAt ? new Date(b.recordedAt) : new Date(b.date || 0);
                return sortDirection === 'asc' 
                    ? dateA - dateB 
                    : dateB - dateA;
            }
            
            // Special handling for duration
            if (sortField === 'durationMs') {
                const durationA = a.durationMs || 0;
                const durationB = b.durationMs || 0;
                return sortDirection === 'asc'
                    ? durationA - durationB
                    : durationB - durationA;
            }
            
            // Special handling for level (combines nBack and difficulty)
            if (sortField === 'level') {
                const levelA = (a.nBack || 0) + (a.difficulty || 0);
                const levelB = (b.nBack || 0) + (b.difficulty || 0);
                return sortDirection === 'asc'
                    ? levelA - levelB
                    : levelB - levelA;
            }
            
            // Default handling for other numeric fields
            if (typeof a[sortField] === 'number' && typeof b[sortField] === 'number') {
                return sortDirection === 'asc'
                    ? a[sortField] - b[sortField]
                    : b[sortField] - a[sortField];
            }
            
            // Default handling for string fields
            const valueA = String(a[sortField] || '');
            const valueB = String(b[sortField] || '');
            return sortDirection === 'asc'
                ? valueA.localeCompare(valueB)
                : valueB.localeCompare(valueA);
        });
    }, [sessions, sortField, sortDirection]);

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-gray-400 opacity-50 h-3 w-3" />;
        }
        return sortDirection === 'asc' 
            ? <FontAwesomeIcon icon={faChevronUp} className="ml-1 text-indigo-500 h-3 w-3" />
            : <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-indigo-500 h-3 w-3" />;
    };

    // Empty state
    if (sessions.length === 0) {
        return (
            <div className="p-8 text-center">
                <p className="text-gray-500 mb-2">No sessions match the current filters.</p>
                <p className="text-sm text-gray-400">Try adjusting the date range or clearing filters.</p>
            </div>
        );
    }

    return (
        <div>
            {/* Desktop View - Standard Table Layout */}
            <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                    {/* Table Header */}
                    <thead className="bg-gray-50">
                        <tr>
                            <th 
                                scope="col" 
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('recordedAt')}
                            >
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-1.5 text-gray-400 h-3 w-3" />
                                    <span>Date</span>
                                    {getSortIcon('recordedAt')}
                                </div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('nBack')}
                            >
                                <div className="flex items-center justify-center">
                                    <span>N</span>
                                    {getSortIcon('nBack')}
                                </div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('durationMs')}
                            >
                                <div className="flex items-center justify-center">
                                    <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-400 h-3 w-3" />
                                    <span>Duration</span>
                                    {getSortIcon('durationMs')}
                                </div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('accuracy')}
                            >
                                <div className="flex items-center justify-center">
                                    <FontAwesomeIcon icon={faPercentage} className="mr-1.5 text-gray-400 h-3 w-3" />
                                    <span>Acc %</span>
                                    {getSortIcon('accuracy')}
                                </div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('visualAccuracy')}
                            >
                                <div className="flex items-center justify-center">
                                    <span>Vis %</span>
                                    {getSortIcon('visualAccuracy')}
                                </div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('audioAccuracy')}
                            >
                                <div className="flex items-center justify-center">
                                    <span>Aud %</span>
                                    {getSortIcon('audioAccuracy')}
                                </div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('level')}
                            >
                                <div className="flex items-center justify-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-1.5 text-gray-400 h-3 w-3" />
                                    <span>Level</span>
                                    {getSortIcon('level')}
                                </div>
                            </th>
                            <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <span>Actions</span>
                            </th>
                        </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedSessions.map((session) => {
                            const rowKey = session.id || `session-${session.recordedAt || session.date}`;
                            const dateObj = session.recordedAt ? new Date(session.recordedAt) : new Date(session.date);

                            const hasValidReplayData = !!session?.replayData?.settings &&
                                Array.isArray(session.replayData.events) &&
                                session.replayData.startTime !== undefined;

                            const canReplay = !!session?.id && hasValidReplayData;
                            const oldCombinedValue = formatCombinedDifficulty(session.nBack, session.difficulty);
                            const newCombinedValue = session.newDifficulty !== undefined && session.newNBack !== undefined
                                ? formatCombinedDifficulty(session.newNBack, session.newDifficulty)
                                : null;

                            const levelChanged = newCombinedValue !== null && newCombinedValue !== oldCombinedValue;
                            const levelIncreased = levelChanged && (
                                (session.newNBack > session.nBack) ||
                                (session.newNBack === session.nBack && session.newDifficulty > session.difficulty)
                            );
                            const levelDecreased = levelChanged && (
                                (session.newNBack < session.nBack) ||
                                (session.newNBack === session.nBack && session.newDifficulty < session.difficulty)
                            );

                            return (
                                <tr key={rowKey} className="hover:bg-gray-50 transition-colors duration-150">
                                    {/* Date Cell */}
                                    <td className="px-4 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-700">
                                        {formatDateTime(dateObj)}
                                    </td>

                                    {/* N-Back Cell */}
                                    <td className="px-2 py-3.5 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 text-center">
                                        <div className="flex flex-col items-center">
                                            <span>{session.nBack}</span>
                                            {session.newNBack !== undefined && session.newNBack !== session.nBack && (
                                                <span className={`text-xs ${session.newNBack > session.nBack ? 'text-green-600' : 'text-red-500'} flex items-center mt-0.5`}>
                                                    <FontAwesomeIcon 
                                                        icon={session.newNBack > session.nBack ? faArrowUp : faArrowDown} 
                                                        className="h-2.5 w-2.5 mr-0.5" 
                                                    />
                                                    {session.newNBack}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Duration Cell */}
                                    <td className="px-3 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">
                                        {formatDuration(session.durationMs)}
                                    </td>

                                    {/* Accuracy Cell */}
                                    <td className="px-3 py-3.5 whitespace-nowrap text-xs sm:text-sm font-medium text-center">
                                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
                                            {session.accuracy?.toFixed(1) ?? '-'}%
                                        </div>
                                    </td>

                                    {/* Visual Accuracy Cell */}
                                    <td className="px-3 py-3.5 whitespace-nowrap text-xs sm:text-sm text-center">
                                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">
                                            {session.visualAccuracy?.toFixed(1) ?? '-'}%
                                        </div>
                                    </td>

                                    {/* Audio Accuracy Cell */}
                                    <td className="px-3 py-3.5 whitespace-nowrap text-xs sm:text-sm text-center">
                                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-800">
                                            {session.audioAccuracy?.toFixed(1) ?? '-'}%
                                        </div>
                                    </td>

                                    {/* Level Cell */}
                                    <td className="px-3 py-3.5 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className="font-medium">{oldCombinedValue}</span>
                                            {levelChanged && (
                                                <span className={`text-xs flex items-center mt-0.5 ${levelIncreased ? 'text-green-600' : 'text-red-500'}`}>
                                                    <FontAwesomeIcon 
                                                        icon={levelIncreased ? faArrowUp : levelDecreased ? faArrowDown : faEquals} 
                                                        className="h-2.5 w-2.5 mr-0.5" 
                                                    />
                                                    {newCombinedValue}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    
                                    {/* Actions Cell */}
                                    <td className="px-4 py-3.5 whitespace-nowrap text-center text-sm font-medium">
                                        {canReplay ? (
                                            <button
                                                onClick={(e) => handleReplay(e, session)}
                                                className="inline-flex items-center text-teal-600 hover:text-teal-800 transition-colors duration-150 p-1.5 rounded hover:bg-teal-50"
                                                title="Replay Session"
                                            >
                                                <FontAwesomeIcon icon={faPlay} className="h-4 w-4 mr-1"/>
                                                <span className="text-xs font-medium">Replay</span>
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 p-1.5 inline-flex items-center" title="Replay Unavailable">
                                                <FontAwesomeIcon icon={faPlay} className="h-4 w-4 mr-1"/>
                                                <span className="text-xs">N/A</span>
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden">
                {sortedSessions.map((session, index) => {
                    const rowKey = session.id || `session-${session.recordedAt || session.date}-${index}`;
                    const dateObj = session.recordedAt ? new Date(session.recordedAt) : new Date(session.date);
                    
                    const hasValidReplayData = !!session?.replayData?.settings &&
                        Array.isArray(session.replayData.events) &&
                        session.replayData.startTime !== undefined;
                    
                    const canReplay = !!session?.id && hasValidReplayData;
                    const oldCombinedValue = formatCombinedDifficulty(session.nBack, session.difficulty);
                    const newCombinedValue = session.newDifficulty !== undefined && session.newNBack !== undefined
                        ? formatCombinedDifficulty(session.newNBack, session.newDifficulty)
                        : null;
                    
                    const levelChanged = newCombinedValue !== null && newCombinedValue !== oldCombinedValue;
                    const levelIncreased = levelChanged && (
                        (session.newNBack > session.nBack) ||
                        (session.newNBack === session.nBack && session.newDifficulty > session.difficulty)
                    );
                    
                    return (
                        <div 
                            key={rowKey}
                            className="border border-gray-200 rounded-lg mb-3 shadow-sm hover:shadow transition overflow-hidden"
                        >
                            {/* Header */}
                            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <span className="text-sm font-medium text-gray-700">
                                    {formatDateTime(dateObj)}
                                </span>
                                
                                <div className="flex items-center">
                                    {/* Level Tag */}
                                    <div className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md text-xs flex items-center mr-3">
                                        <span>Level {oldCombinedValue}</span>
                                        {levelChanged && (
                                            <span className={`ml-1 ${levelIncreased ? 'text-green-600' : 'text-red-500'}`}>
                                                <FontAwesomeIcon 
                                                    icon={levelIncreased ? faArrowUp : faArrowDown} 
                                                    className="h-2.5 w-2.5" 
                                                />
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* N-Back Tag */}
                                    <div className="bg-blue-50 border border-blue-100 text-blue-700 px-2 py-0.5 rounded-md text-xs font-medium">
                                        {session.nBack}-back
                                    </div>
                                </div>
                            </div>
                            
                            {/* Body */}
                            <div className="p-4">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-3 gap-2 mb-4">
                                    {/* Duration */}
                                    <div className="bg-gray-50 rounded p-2 text-center">
                                        <div className="text-xs text-gray-500 mb-1">Duration</div>
                                        <div className="text-sm font-medium">{formatDuration(session.durationMs)}</div>
                                    </div>
                                    
                                    {/* Accuracy */}
                                    <div className="bg-gray-50 rounded p-2 text-center">
                                        <div className="text-xs text-gray-500 mb-1">Accuracy</div>
                                        <div className="text-sm font-medium">{session.accuracy?.toFixed(0) ?? '-'}%</div>
                                    </div>
                                    
                                    {/* Visual/Audio Split */}
                                    <div className="bg-gray-50 rounded p-2 text-center">
                                        <div className="text-xs text-gray-500 mb-1">V/A Split</div>
                                        <div className="text-xs">
                                            <span className="text-blue-600 font-medium">{session.visualAccuracy?.toFixed(0) ?? '-'}%</span>
                                            <span className="mx-0.5 text-gray-400">/</span>
                                            <span className="text-green-600 font-medium">{session.audioAccuracy?.toFixed(0) ?? '-'}%</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Action Button */}
                                <div className="flex justify-end">
                                    {canReplay ? (
                                        <button
                                            onClick={(e) => handleReplay(e, session)}
                                            className="inline-flex items-center text-white bg-teal-600 hover:bg-teal-700 transition-colors duration-150 py-1.5 px-3 rounded-md text-sm"
                                        >
                                            <FontAwesomeIcon icon={faPlay} className="h-3.5 w-3.5 mr-1.5"/>
                                            Replay Session
                                        </button>
                                    ) : (
                                        <button disabled className="inline-flex items-center bg-gray-200 text-gray-500 py-1.5 px-3 rounded-md text-sm cursor-not-allowed">
                                            <FontAwesomeIcon icon={faQuestionCircle} className="h-3.5 w-3.5 mr-1.5"/>
                                            Replay Unavailable
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AllSessionsTable;